import { PacProcedure, PROCEDURE_AVERAGE_LOSS } from '@bariendo/lib/patient-access-script';
import { observationWeightToKg } from '@bariendo/lib/bmi';
import { Observation } from '@medplum/fhirtypes';
import { kgToLbs } from '@/services/patient';

export interface WeightLossEstimate {
  /** Expected loss in whole pounds. */
  lossLb: number;
  /** The patient's starting weight in whole pounds. */
  startingLb: number;
  /** Average loss for the procedure, as a whole percent. Shown as working, never spoken. */
  percent: number;
}

/**
 * Weight-loss estimate for the "Explain the procedure" beat: the patient's
 * weight times the procedure average.
 *
 * Returned in pounds with the working alongside. The percentage is for the
 * rep's chart only — per the script it is never said to the patient, who hears
 * the pound figure.
 *
 * Returns undefined when either input is missing, so the card can prompt the
 * rep to confirm the procedure rather than render a wrong number.
 */
export function calculateWeightLossEstimate(
  weightObservation: Observation | undefined,
  procedure: string | undefined
): WeightLossEstimate | undefined {
  if (!procedure || !(procedure in PROCEDURE_AVERAGE_LOSS)) {
    return undefined;
  }

  const kg = observationWeightToKg(weightObservation);
  if (kg === undefined) {
    return undefined;
  }

  const startingLb = kgToLbs(kg);
  if (!startingLb || startingLb <= 0) {
    return undefined;
  }

  const average = PROCEDURE_AVERAGE_LOSS[procedure as PacProcedure];

  return {
    lossLb: Math.round(startingLb * average),
    startingLb,
    percent: Math.round(average * 100),
  };
}

/** e.g. "~46 lb". */
export function formatEstimateValue(estimate: WeightLossEstimate): string {
  return `~${estimate.lossLb} lb`;
}

/** e.g. "232 lb × 20%". */
export function formatEstimateWorking(estimate: WeightLossEstimate): string {
  return `${estimate.startingLb} lb × ${estimate.percent}%`;
}
